import { useRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { graphql, createPaginationContainer } from 'react-relay';
import { useSelector } from 'react-redux';
import {
    trackCarouselEvent,
    trackSellerPageClick,
    trackSellerItemClick,
    trackCarouselItemImpression,
} from '../../tracking/similarItemsTracking';
import { locations } from '../../../utils/tracking/shared/favoritesTracking';

// components
import { Carousel } from 'dibs-carousel';
import { FormattedMessage } from 'dibs-react-intl';
import { SeoButtonComponent } from 'dibs-seo/exports/SeoButton';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import {
    SharedRespRelatedItem,
    X_SMALL_TILE_TYPE,
} from '../../../shared/SharedRespRelatedItem/SharedRespRelatedItem';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { QuickViewProvider } from '../../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../../components/global/FavoriteIcons/FavoritesProvider';

// styles
import styles from './PdpRespSellerItemTileCarousel.scss';

const ITEMS_PER_PAGE = 5;
const ITEMS_REFETCH_LIMIT = 9;

export function PdpRespSellerItemTileCarouselComponent({
    item,
    user,
    relay,
    viewer,
    currency,
    isViewAllTopAligned,
}) {
    const { sellerItems } = viewer || {};
    const { serviceId: userId } = user || {};
    const { serviceId: itemPk, seller } = item || {};
    const { serviceId: sellerPk } = seller || {};
    const hasSellerItems = sellerItems?.edges?.length;
    const carouselRef = useRef(null);
    const impressionTrackingRef = useRef({});
    const [carouselPage, setCarouselPage] = useState(0);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [isCarouselVisible, setIsCarouselVisible] = useState(false);
    const [hasFetchedAllImages, setHasFetchedAllImages] = useState(false);

    const { isClient } = useSelector(state => state.relayVariables.variables);

    const renderItem = useCallback(
        ({ index, quickView, favorites }) => {
            const similarItemEdge = sellerItems?.edges?.[index];
            const similarItemNode = similarItemEdge?.node;

            if (!similarItemNode) {
                return null;
            }

            return (
                <SharedRespRelatedItem
                    isMobile // setting to isMobile for quickview styles
                    showAuctionBids
                    hideTileImagePadding
                    user={user}
                    index={index}
                    viewer={viewer}
                    item={similarItemNode}
                    currency={currency}
                    quickView={quickView}
                    showMeasurements={false}
                    onClick={() => trackSellerItemClick(index, similarItemEdge)}
                    favorites={favorites}
                    tileType={X_SMALL_TILE_TYPE}
                />
            );
        },
        [sellerItems, user, viewer, currency]
    );

    const onArrowClick = useCallback(
        direction => {
            if (!hasFetchedAllImages) {
                setHasFetchedAllImages(true);
                relay.loadMore(ITEMS_REFETCH_LIMIT);
            }
            trackCarouselEvent(direction);
        },
        [hasFetchedAllImages, relay]
    );

    useEffect(() => {
        // if user logs in after paginating pdp refetch container resets this pagination container
        // in this case reset carousel
        if (userId) {
            setCarouselIndex(0);
            setHasFetchedAllImages(false);
        }
    }, [userId]);

    useEffect(() => {
        if (!isCarouselVisible) {
            return;
        }

        const startIndex = carouselPage * ITEMS_PER_PAGE;
        const visibleItems =
            sellerItems?.edges?.slice(startIndex, startIndex + ITEMS_PER_PAGE) || [];

        visibleItems.forEach(({ node }, index) => {
            const { ecommerceTrackingParams, categoryCode, categoryPath } = node || {};
            const { id } = ecommerceTrackingParams || {};

            if (!impressionTrackingRef.current[id]) {
                trackCarouselItemImpression({
                    ...node.ecommerceTrackingParams,
                    category: categoryPath,
                    categoryCode,
                    position: Math.floor(startIndex + index),
                });
                impressionTrackingRef.current[id] = true;
            }
        });
    }, [carouselPage, sellerItems, isCarouselVisible]);

    if (!hasSellerItems) {
        return null;
    }

    return (
        <div
            className={classnames(styles.container, {
                [styles.isViewAllTopAligned]: isViewAllTopAligned,
            })}
            ref={carouselRef}
            data-tn="pdp-dealer-grid"
        >
            <span className={styles.title}>
                <FormattedMessage
                    defaultMessage="More From This Seller"
                    id="PdpRespSellerItemTileCarousel.title"
                />
            </span>
            {isViewAllTopAligned && (
                <SeoLink
                    className={styles.viewAllLink}
                    dataTn="pdp-condensed-view-all-seller"
                    linkData={{
                        path: `/more-from-seller/?itemPk=${itemPk}&sellerPk=${sellerPk}`,
                        isLinkable: false,
                    }}
                >
                    <FormattedMessage
                        defaultMessage="View All"
                        id="PdpRespSellerItemTileCarousel.viewAll"
                    />
                </SeoLink>
            )}
            <VisibilityTracker
                elementRef={carouselRef}
                onVisibilityChange={({ isVisible }) => setIsCarouselVisible(isVisible)}
            />
            <FavoritesProvider
                location={locations.SELLER_GRID}
                disable={!isClient}
                userId={userId || null}
                environment={relay.environment}
                itemIds={sellerItems?.edges?.map(edge => edge?.node?.serviceId) || []}
            >
                {({ props: favorites }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <Carousel
                                isInfinite
                                slideCarouselItems
                                step={ITEMS_PER_PAGE}
                                index={carouselIndex}
                                itemsPerPage={ITEMS_PER_PAGE}
                                totalItems={sellerItems?.edges?.length}
                                onLeftArrowClick={() => onArrowClick('left')}
                                onRightArrowClick={() => onArrowClick('right')}
                                onPageChange={({ page }) => setCarouselPage(page)}
                                onIndexChange={({ index }) => setCarouselIndex(index)}
                                renderItem={({ index }) =>
                                    renderItem({ index, quickView, favorites })
                                }
                                classNames={{
                                    item: styles.item,
                                    arrowLeft: styles.arrowLeft,
                                    arrowRight: styles.arrowRight,
                                    list: styles.list,
                                }}
                            />
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
            {!isViewAllTopAligned && (
                <div className={styles.viewAll}>
                    <SeoButtonComponent
                        size="small"
                        type="secondary"
                        onClick={trackSellerPageClick}
                        linkData={{
                            path: `/more-from-seller/?itemPk=${itemPk}&sellerPk=${sellerPk}`,
                            isLinkable: false,
                        }}
                        dataTn="view-more-from-seller-button"
                    >
                        <FormattedMessage
                            defaultMessage="View All From Seller"
                            id="PdpRespSellerItemTileCarousel.viewAllFromSeller"
                        />
                    </SeoButtonComponent>
                </div>
            )}
        </div>
    );
}

PdpRespSellerItemTileCarouselComponent.propTypes = {
    user: PropTypes.object,
    currency: PropTypes.string,
    item: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    isViewAllTopAligned: PropTypes.bool,
};

export const PdpRespSellerItemTileCarousel = createPaginationContainer(
    PdpRespSellerItemTileCarouselComponent,
    {
        viewer: graphql`
            fragment PdpRespSellerItemTileCarousel_viewer on Viewer
            @argumentDefinitions(
                after: { type: "String" }
                first: { type: "Int", defaultValue: 6 }
                userCountryCode: { type: "String" }
            ) {
                ...SharedRespRelatedItem_viewer
                sellerItems: moreLikeThisItem(
                    first: $first
                    after: $after
                    seller: INCLUDE
                    itemId: $itemId
                ) @connection(key: "PdpRespSellerItemTileCarousel_sellerItems", filters: []) {
                    edges {
                        node {
                            ecommerceTrackingParams
                            categoryPath
                            categoryCode
                            serviceId
                            browseUrl
                            title
                            ...SharedRespRelatedItem_item
                                @arguments(userCountryCode: $userCountryCode)
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespSellerItemTileCarousel_user on User {
                serviceId
                ...SharedRespRelatedItem_user
            }
        `,
        item: graphql`
            fragment PdpRespSellerItemTileCarousel_item on Item {
                serviceId
                seller {
                    serviceId
                }
            }
        `,
    },
    {
        direction: 'forward',
        getConnectionFromProps(props) {
            return props.viewer && props.viewer.sellerItems;
        },
        getFragmentVariables(prevVars, first) {
            return {
                ...prevVars,
                first,
            };
        },
        getVariables(props, { count, cursor }, fragmentVariables) {
            return {
                ...fragmentVariables,
                first: count,
                after: cursor,
            };
        },
        query: graphql`
            query PdpRespSellerItemTileCarouselRefetchQuery(
                $itemId: String!
                $isTrade: Boolean!
                $after: String
                $first: Int!
                $userCountryCode: String
            ) {
                viewer {
                    ...PdpRespSellerItemTileCarousel_viewer
                        @arguments(first: $first, after: $after, userCountryCode: $userCountryCode)
                }
            }
        `,
    }
);
